import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import { TextSkeleton } from "../../interfaccia/loading";
import ComposizioneArticolo from "./kitModa/ComposizioneArticolo";
import InvioTaglieModal, { candidateInvio } from "./kitModa/InvioTaglieModal";
import WizardKitTaglia from "./kitModa/WizardKitTaglia";
import {
  creaApiKitModa,
  etichettaTaglia,
  isAnnullata,
  messaggioErrore,
  richiedeVerifica,
} from "./kitModa/kitModaApi";
import "./kitModa/KitModa.css";

const ESITI = {
  KIT: { label: "Kit", className: "scrittaColAltScuro b" },
  NESSUN_KIT: { label: "Nessun kit", className: "scrittaColMedio" },
};

const CONFRONTI = {
  ALLINEATO: { label: "Come la composizione", className: "scrittaColAltScuro" },
  DIVERSO: { label: "Diverso dalla composizione", className: "scrittaColMedio" },
  DA_COMPORRE: { label: "Da comporre", className: "scrittaColMedio" },
};

const formatDataOra = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function ComponentiKit({ riga, codVS }) {
  const { kit, componenti } = riga.cache;
  return (
    <div className="f6">
      {kit?.codiceArticolo && String(kit.codiceArticolo) !== String(codVS) && (
        <p className="ma0 mb1 dark-red" role="alert">
          Tembo associa questo kit al codVS {kit.codiceArticolo}, non a {codVS}.
        </p>
      )}
      {componenti.length === 0 ? (
        <span>Tembo non ha restituito componenti.</span>
      ) : (
        <ul className="list pl0 ma0" aria-label={`Componenti del kit ${riga.tripletta}`}>
          {componenti.map((componente) => (
            <li key={componente.posizione} className="pv1">
              <span className="b">{componente.itemsInKit} ×</span> {componente.reference || "—"}{" "}
              {componente.modaNome || componente.name || ""}
              <span className="db scrittaColMedio">
                {componente.itemAttributes || "Taglia non indicata"}
                {componente.itemReference ? ` · ${componente.itemReference}` : ""}
              </span>
              {componente.reference && !componente.modaNome && (
                <span className="db scrittaColMedio">Non presente fra gli articoli Moda di Domino</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KitModa({ codVS, utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const [stato, setStato] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [avviso, setAvviso] = useState("");
  const [verifica, setVerifica] = useState(null);
  const [erroriVerifica, setErroriVerifica] = useState([]);
  const [wizard, setWizard] = useState(null);
  const [invioMultiplo, setInvioMultiplo] = useState(false);
  const mounted = useRef(false);
  const verificaInCorso = useRef(false);

  const username = typeof utente?.username === "string" ? utente.username.trim() : "";
  const puoAggiornareDomino = Boolean(utente?.permessiAPI?.["update:database"]);
  const puoScrivereKit = puoAggiornareDomino && Boolean(utente?.permessiAPI?.["update:prestashop"]);
  const api = useMemo(
    () => creaApiKitModa({ getAccessTokenSilently, username }),
    [getAccessTokenSilently, username],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setStato(null);
    setError("");
    setAvviso("");
    setErroriVerifica([]);
    setWizard(null);
    if (!codVS) return undefined;
    const controller = new AbortController();
    setLoading(true);
    api
      .stato(codVS, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setStato(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAnnullata(err)) return;
        setError(messaggioErrore(err, "Errore nel recupero dei kit Moda"));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [api, codVS]);

  const avviaVerifica = useCallback(
    async (triplette) => {
      if (verificaInCorso.current || !codVS || !puoAggiornareDomino || !username) return null;
      verificaInCorso.current = true;
      setVerifica(triplette ? triplette[0] : "tutte");
      setError("");
      setAvviso("");
      setErroriVerifica([]);
      try {
        const data = await api.verifica(codVS, triplette);
        if (!mounted.current) return null;
        if (data) setStato(data);
        setErroriVerifica(Array.isArray(data?.errori) ? data.errori : []);
        return data;
      } catch (err) {
        if (mounted.current) setError(messaggioErrore(err, "Verifica dei kit non riuscita"));
        return null;
      } finally {
        verificaInCorso.current = false;
        if (mounted.current) setVerifica(null);
      }
    },
    [api, codVS, puoAggiornareDomino, username],
  );

  /** Prima di modificare una taglia serve il kit attuale letto da Prestashop. */
  const apriWizard = async (riga) => {
    setAvviso("");
    if (!richiedeVerifica(riga)) {
      setWizard(riga.tripletta);
      return;
    }
    const data = await avviaVerifica([riga.tripletta]);
    const aggiornata = data?.righe?.find((item) => item.tripletta === riga.tripletta);
    if (aggiornata && !richiedeVerifica(aggiornata) && !data.errori?.length) setWizard(riga.tripletta);
  };

  const righe = stato?.righe ?? [];
  const rigaWizard = wizard ? righe.find((riga) => riga.tripletta === wizard) : null;
  const tutteVerificate = righe.length > 0 && righe.every((riga) => riga.cache);
  const kitTrovati = righe.filter((riga) => riga.cache?.esito === "KIT").length;
  const daInviare = stato ? candidateInvio(righe).length : 0;
  const erroriPerTripletta = new Map(erroriVerifica.map((item) => [item.tripletta, item.error]));
  const motivoBlocco = !puoAggiornareDomino
    ? "Serve il permesso update:database per verificare i kit."
    : !username
      ? "Username Domino mancante: ricarica il profilo utente prima di verificare."
      : "";

  return (
    <section className="modificaProdottiPagina modificaProdottiKitModa pa3" aria-label="Kit Moda">
      <div className="flex flex-wrap items-start justify-between mb3">
        <div className="mr3 mb2" style={{ maxWidth: "46rem" }}>
          <h3 className="mt0 mb2">Kit Moda</h3>
          <p className="mt0 mb0 lh-copy scrittaColMedio">
            Articoli Moda composti da altri articoli Moda, con la quantità di ciascun
            componente. Il legame vive su Prestashop Viglietti, una taglia alla volta:
            la verifica lo legge da Tembo e lo conserva in Domino. Non riguarda il kit
            virtuale del modulo kit di Prestashop.
          </p>
        </div>
        <div>
          <button
            type="button"
            className={stilePulsanti.buttonClassesWhite}
            onClick={() => avviaVerifica(null)}
            disabled={Boolean(verifica) || loading || !righe.length || Boolean(motivoBlocco)}
          >
            {verifica === "tutte"
              ? "Verifica in corso..."
              : tutteVerificate
                ? "Riverifica kit su Prestashop"
                : "Verifica kit su Prestashop"}
          </button>
          {puoScrivereKit && Boolean(username) && stato?.schema?.length > 0 && (
            <button
              type="button"
              className={stilePulsanti.buttonClassesWhite}
              onClick={() => setInvioMultiplo(true)}
              disabled={Boolean(verifica) || daInviare === 0}
            >
              Applica la composizione alle taglie{daInviare ? ` (${daInviare})` : ""}
            </button>
          )}
        </div>
      </div>

      {motivoBlocco && <p className="mt0 mb3 f6 scrittaColMedio">{motivoBlocco}</p>}
      {!puoScrivereKit && puoAggiornareDomino && (
        <p className="mt0 mb3 f6 scrittaColMedio">
          Per creare o modificare i kit su Prestashop serve anche il permesso update:prestashop.
        </p>
      )}
      {error && (
        <p className="mt0 mb3 dark-red" role="alert">
          {error}
        </p>
      )}
      {avviso && (
        <p className="mt0 mb3 dark-red" role="status">
          {avviso}
        </p>
      )}
      {erroriVerifica.length > 0 && (
        <p className="mt0 mb3 dark-red" role="alert">
          {erroriVerifica.length === 1
            ? "1 taglia non è stata verificata."
            : `${erroriVerifica.length} taglie non sono state verificate.`}{" "}
          Le altre sono aggiornate.
        </p>
      )}

      {stato && (
        <ComposizioneArticolo
          codVS={codVS}
          stato={stato}
          api={api}
          puoModificare={puoAggiornareDomino && Boolean(username)}
          onStato={setStato}
        />
      )}

      {loading && !stato ? (
        <TextSkeleton lines={4} lineHeight={18} />
      ) : !stato ? null : righe.length === 0 ? (
        <p className="mt0">
          Nessuna taglia con barcode attivo in Moda per il codVS {codVS}: non c'è niente da verificare.
        </p>
      ) : (
        <>
          {tutteVerificate && (
            <p className="mt0 mb2 f6">
              {kitTrovati === 0
                ? "Nessuna taglia di questo articolo è un kit."
                : kitTrovati === 1
                  ? "1 taglia è un kit."
                  : `${kitTrovati} taglie sono kit.`}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="collapse w-100" aria-label="Taglie Moda e kit">
              <thead>
                <tr className="tl bb bordoColAlt">
                  <th className="pv2 pr3">Taglia</th>
                  <th className="pv2 pr3">Stato</th>
                  <th className="pv2 pr3">Componenti</th>
                  <th className="pv2" aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {righe.map((riga) => {
                  const esito = riga.cache ? ESITI[riga.cache.esito] : null;
                  const confronto = riga.confronto ? CONFRONTI[riga.confronto] : null;
                  const erroreRiga = erroriPerTripletta.get(riga.tripletta);
                  return (
                    <tr key={riga.tripletta} className="bt bordoColAlt v-top">
                      <td className="pv2 pr3">
                        {etichettaTaglia(riga)}
                        <span className="db f7 scrittaColMedio">
                          {riga.tripletta}
                          {riga.barcodes?.length ? ` · ${riga.barcodes.join(", ")}` : ""}
                        </span>
                        {!riga.inModa && (
                          <span className="db f6 dark-red">Non più fra i barcode attivi in Moda</span>
                        )}
                      </td>
                      <td className="pv2 pr3">
                        {esito ? (
                          <span className={esito.className}>{esito.label}</span>
                        ) : (
                          <span className="scrittaColMedio">Non verificato</span>
                        )}
                        {confronto && riga.cache && (
                          <span className={`db f7 ${confronto.className}`}>{confronto.label}</span>
                        )}
                        {riga.cache?.verificatoAt && (
                          <span className="db f7 scrittaColMedio">
                            {formatDataOra(riga.cache.verificatoAt)}
                            {riga.cache.verificatoDa ? ` · ${riga.cache.verificatoDa}` : ""}
                          </span>
                        )}
                        {erroreRiga && <span className="db f6 dark-red">{erroreRiga}</span>}
                      </td>
                      <td className="pv2 pr3">
                        {riga.cache?.esito === "KIT" ? <ComponentiKit riga={riga} codVS={codVS} /> : "—"}
                      </td>
                      <td className="pv2 tr kitModaAzioni">
                        {riga.inModa && (
                          <>
                            {puoScrivereKit && Boolean(username) && (
                              <button
                                type="button"
                                className={stilePulsanti.buttonClassesWhite}
                                onClick={() => apriWizard(riga)}
                                disabled={Boolean(verifica)}
                                aria-label={`${riga.cache?.esito === "KIT" ? "Modifica" : "Componi"} kit ${riga.tripletta}`}
                              >
                                {riga.cache?.esito === "KIT" ? "Modifica" : "Componi"}
                              </button>
                            )}
                            <button
                              type="button"
                              className={stilePulsanti.buttonClassesWhite}
                              onClick={() => avviaVerifica([riga.tripletta])}
                              disabled={Boolean(verifica) || Boolean(motivoBlocco)}
                              aria-label={`Verifica kit ${riga.tripletta}`}
                            >
                              {verifica === riga.tripletta ? "..." : riga.cache ? "Riverifica" : "Verifica"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {rigaWizard && (
        <WizardKitTaglia
          codVS={codVS}
          riga={rigaWizard}
          stato={stato}
          api={api}
          onClose={() => setWizard(null)}
          onSent={(data) => {
            setWizard(null);
            if (data) setStato(data);
            setAvviso(data?.avviso || "");
          }}
          onConflict={(data) => setStato(data)}
        />
      )}
      {invioMultiplo && stato && (
        <InvioTaglieModal codVS={codVS} stato={stato} api={api} onClose={() => setInvioMultiplo(false)} onStato={setStato} />
      )}
    </section>
  );
}

export default KitModa;
